import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Dialog from 'components/dialog';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';

import React, { useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { ScenarioModel } from 'types/shared/scenario-model-type.enum';
import titleCase from 'utils/title-case';
import { ScenariosParams } from 'views/bluviu-nutrition/features/scenarios/types/scenarios-params.type';
import { useSelectedSite } from '../../../hooks/use-selected-site';
import { useCreateScenario } from '../../scenarios/hooks/use-create-scenario';
import { CreateScenarioRequest } from '../../scenarios/types/create-scenario-request.type';

const PAGE_NAME_ID = 'bluViuNutrition.listScenarios';

type Props = { invalidNames: string[] };

const CreateCalibrationDialog: React.FC<Props> = ({ invalidNames }) => {
  const {
    account: { company },
  } = useAccountState();
  const { createScenario } = useCreateScenario();
  const { selectedSiteId, selectedSite } = useSelectedSite();
  const history = useHistory();
  const intl = useIntl();
  const params = useParams<ScenariosParams>();

  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const defaultValues = useMemo(
    () => ({
      name: '',
      globalSiteId: params.globalSiteId,
    }),
    [params.globalSiteId]
  );

  const { handleSubmit, control, reset } = useForm<CreateScenarioRequest>({
    defaultValues: defaultValues,
    shouldUnregister: true,
  });

  const onSubmit = (data: CreateScenarioRequest) => {
    console.log(data);

    createScenario.mutate(
      {
        globalCompanyId: company!.companyId,
        request: {
          ...data,
          globalSiteId: params.globalSiteId,
        },
      },
      {
        onSuccess: (scenario) => {
          reset();
          dialogRef.current?.setOpen(false);
          history.push(`/bluviu-nutrition/scenarios/${selectedSiteId}/${scenario.id}`);
        },
      }
    );
  };

  return (
    <Dialog
      ref={dialogRef}
      maxWidth={'sm'}
      title={`${intl.formatMessage({ id: `${PAGE_NAME_ID}.createDialog.title` })}`}
      button={
        <Button>
          <FormattedMessage id={`${PAGE_NAME_ID}.createDialog.title`} />
        </Button>
      }>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography fontSize={14}>For site: {selectedSite?.name}</Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <TextField
              dark
              fullWidth
              controller={{
                control,
                name: 'name',
                rules: {
                  required: true,
                  validate: (value) => (!invalidNames.includes(value) ? true : `Name must not match an existing scenario.`),
                },
              }}
              label={'Scenario name'}
            />
            <TextField fullWidth dark label={'Model'} disabled value={titleCase(ScenarioModel.EFFECTIVE_ENERGY_GT_2)} />
            <Box display={'flex'} justifyContent={'end'}>
              <Button
                type="submit"
                disabled={createScenario.isPending}
                startIcon={createScenario.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
                <FormattedMessage id={`general.create`} values={{ type: '' }} />
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Dialog>
  );
};

export default React.memo(CreateCalibrationDialog);
